// Uncomment to test if any styles leaked to Microfrontend
// import './App.css';

import { useAuth0 } from '@auth0/auth0-react';
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';

import { MountAsWidget } from './MountAsWidget';
import { MountToContainer } from './MountToContainer';
import { PentestPage } from './pentest/PentestPage';
import ProtectedRoute from './ProtectedRoute';

// The pentest build must not ship the authenticated routes (HostLayout reads the UP token env vars).
const isPentestBuild = import.meta.env.MODE === 'pentest';

// Code-split: HostLayout carries the UP token options, so the open /pentesting route never loads it.
const HostLayout = isPentestBuild
  ? null
  : lazy(() => import('./HostLayout').then((m) => ({ default: m.HostLayout })));

// Code-split: the IDP page (and its React Aria deps) load only when /ai-idp is visited,
// keeping them out of the main chat host-app bundle.
const AiIdp = isPentestBuild
  ? null
  : lazy(() => import('./AiIdp/AiIdp').then((m) => ({ default: m.AiIdp })));

const App = () => {
  const { isAuthenticated, isLoading, getIdTokenClaims } = useAuth0();

  // Store and refresh Auth0 token in localStorage and handle redirect
  useEffect(() => {
    let refreshInterval: ReturnType<typeof setTimeout> | null = null;

    const storeAuth0Token = async () => {
      if (isAuthenticated && !isLoading) {
        try {
          // Get the ID token from Auth0
          const claims = await getIdTokenClaims();
          if (claims && claims.__raw) {
            // Store the raw JWT token in localStorage
            localStorage.setItem('auth0Token', claims.__raw);

            // Get token expiration time from claims
            if (claims.exp) {
              const expirationTime = claims.exp * 1000; // Convert to milliseconds
              const currentTime = Date.now();
              const timeUntilExpiry = expirationTime - currentTime;

              // If token is valid for more than 5 minutes, set refresh to occur 5 minutes before expiry
              if (timeUntilExpiry > 5 * 60 * 1000) {
                const refreshTime = timeUntilExpiry - 5 * 60 * 1000;

                // Clear existing interval if any
                if (refreshInterval) {
                  clearTimeout(refreshInterval);
                }

                // Set timeout to refresh token before it expires
                refreshInterval = setTimeout(storeAuth0Token, refreshTime);
              }
            }
          }
        } catch (error) {
          // Authentication error handling
          console.error('Failed to get Auth0 token:', error); //eslint-disable-line
        }
      }
    };

    storeAuth0Token();

    // Handle redirect to home page after successful authentication
    const params = new URLSearchParams(window.location.search);
    if (params.has('code') && params.has('state') && isAuthenticated && !isLoading) {
      window.history.replaceState({}, document.title, '/');
    }

    // Cleanup function to clear interval when component unmounts
    return () => {
      if (refreshInterval) {
        clearTimeout(refreshInterval);
      }
    };
  }, [isAuthenticated, isLoading, getIdTokenClaims]);

  return (
    <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, '') || '/'}>
      <Routes>
        <Route path='/pentesting' element={<PentestPage />} />
        {isPentestBuild || !HostLayout || !AiIdp ? (
          <>
            <Route path='/' element={<PentestPage />} />
            <Route path='*' element={<Navigate to='/' replace />} />
          </>
        ) : (
          <>
            <Route element={<ProtectedRoute />}>
              <Route
                element={
                  <Suspense fallback={<div className='p-6 text-slate-500'>Loading…</div>}>
                    <HostLayout />
                  </Suspense>
                }>
                <Route path='/' element={<MountAsWidget />} />
                <Route path='/fixed' element={<MountToContainer />} />
                <Route path='/ai-agents/:env' element={<MountAsWidget />} />
                <Route path='/ai-agents/:env/fixed' element={<MountToContainer />} />
              </Route>
              <Route
                path='/ai-idp'
                element={
                  <Suspense fallback={<div className='p-6 text-slate-500'>Loading…</div>}>
                    <AiIdp />
                  </Suspense>
                }
              />
            </Route>
            <Route path='/login' element={<div>Redirecting to login...</div>} />
          </>
        )}
      </Routes>
    </BrowserRouter>
  );
};

export default App;
